/**
 * GameBoy shell — css-gameboy visual (1:1 copy of attackemartin/css-gameboy).
 *
 * The shell HTML mirrors the original CSS-Gameboy markup exactly so the
 * 974-line stylesheet in gameboy-shell.css applies without modification.
 *
 * React children render inside `.screen > .gb-lcd-content` — a DOM container
 * styled to look like a GameBoy LCD (160×144-ish, GameBoy palette, pixel fonts).
 *
 * Buttons dispatch via the parent's onUp/onDown/onLeft/onRight/onA/onB/
 * onSelect/onStart callbacks (same contract as before).
 */
import { type ReactNode, useEffect, useRef } from 'react';

interface GameBoyShellProps {
  children: ReactNode;
  showControls?: boolean;
  onUp?: () => void;
  onDown?: () => void;
  onLeft?: () => void;
  onRight?: () => void;
  onA?: () => void;
  onB?: () => void;
  onSelect?: () => void;
  onStart?: () => void;
  pressedDir?: 'up' | 'down' | 'left' | 'right' | null;
  pressedButton?: 'a' | 'b' | 'select' | 'start' | null;
  showLabel?: boolean;
}

const FONT_HEAD = '"Press Start 2P", monospace';
const FONT_BODY = '"VT323", monospace';

export function GameBoyShell({
  children,
  showControls = true,
  onUp,
  onDown,
  onLeft,
  onRight,
  onA,
  onB,
  onSelect,
  onStart,
}: GameBoyShellProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const gameboyRef = useRef<HTMLDivElement>(null);

  // Detección de dispositivo táctil (mobile / tablet puros).
  // Marca el wrap con `.is-touch-device` cuando el dispositivo
  // NO tiene hover y su puntero primario es coarse (dedo / stylus
  // primario). Esto es la firma de "esto es un teléfono o tablet"
  // — las laptops/surfaces con touch secundario tienen hover
  // disponible, así que no caen en este caso. La clase queda
  // disponible para CSS condicional dentro del wrap y para
  // cualquier lógica futura de scale/layout del shell.
  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    const update = () => {
      wrapRef.current?.classList.toggle('is-touch-device', mq.matches);
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Detección de "modo teclado": cuando un <input>, <textarea> o
  // elemento contenteditable recibe foco, el shell se transiciona
  // a "solo LCD + frame superior" (.keyboard-open) — se ocultan el
  // D-pad, A/B, SELECT/START, speaker, phones y power button para
  // dar más espacio vertical al LCD + teclado nativo del OS.
  //
  // Esto reemplaza el GBKeypad on-screen (eliminado): el usuario
  // siempre escribe con el teclado nativo del dispositivo, y el
  // shell se adapta para darle espacio.
  useEffect(() => {
    function isTextEntry(el: EventTarget | null): boolean {
      if (!(el instanceof HTMLElement)) return false;
      if (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA') return true;
      return el.isContentEditable;
    }
    function update() {
      const wrap = wrapRef.current;
      if (!wrap) return;
      // document.activeElement es lo más confiable para saber si hay
      // un input con foco en este momento (cubre cambios programáticos
      // y el ciclo focusin → focusout entre inputs).
      const active = document.activeElement;
      wrap.classList.toggle('keyboard-open', isTextEntry(active));
    }
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    return () => {
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
    };
  }, []);

  // Responsive scaling: keep aspect ratio 660:1080 (or 660:560 in
  // keyboard-open mode where the bottom half is clipped), anchor to
  // top, recompute when the OS keyboard appears/disappears.
  useEffect(() => {
    const GAMEBOY_W = 660;
    const GAMEBOY_H = 1080;
    const GAMEBOY_H_KEYBOARD = 560; // top half only — LCD + frame superior
    const LCD_W = 160;
    const LCD_H = 144;

    function applyScale() {
      const wrap = wrapRef.current;
      const gb = gameboyRef.current;
      if (!wrap || !gb) return;
      const rect = wrap.getBoundingClientRect();
      const cs = getComputedStyle(wrap);
      const padTop = parseFloat(cs.paddingTop) || 0;
      const padBottom = parseFloat(cs.paddingBottom) || 0;
      const availW = rect.width;
      const availH = rect.height - padTop - padBottom;

      // En modo teclado usamos una altura de shell reducida (solo LCD +
      // frame top) para que el shell quepa cómodo arriba mientras el
      // teclado nativo ocupa la mitad inferior de la pantalla.
      const isKeyboardOpen = wrap.classList.contains('keyboard-open');
      const targetH = isKeyboardOpen ? GAMEBOY_H_KEYBOARD : GAMEBOY_H;
      const scale = Math.min(availW / GAMEBOY_W, availH / targetH);
      // Combine scale + translate(-50%,-50%) for absolute centering.
      // translate(-50%,-50%) offsets by half the element's own size, so
      // top:50%/left:50% truly centers the element even when it's larger
      // than the viewport (the scale then shrinks it visually from center).
      gb.style.transform = `translate(-50%, -50%) scale(${scale})`;
      gb.style.height = `${targetH}px`;

      // Calcular el tamaño LAYOUT del .screen (no el visual, que ya tiene
      // el scale del gameboy aplicado). Usamos offsetWidth/Height que
      // NO se ven afectados por transform — así el LCD compensa el scale
      // del shell y termina visualmente del tamaño correcto.
      const screenEl = gb.querySelector('.screen') as HTMLElement | null;
      if (screenEl) {
        const lcdScale = Math.min(
          screenEl.offsetWidth / LCD_W,
          screenEl.offsetHeight / LCD_H,
        );
        const lcd = gb.querySelector('.gb-lcd-content') as HTMLElement | null;
        if (lcd) lcd.style.setProperty('--lcd-scale', String(lcdScale));
      }
    }

    applyScale();
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', applyScale);
      window.visualViewport.addEventListener('scroll', applyScale);
    }
    window.addEventListener('resize', applyScale);
    window.addEventListener('orientationchange', () => setTimeout(applyScale, 150));
    if (document.fonts?.ready) document.fonts.ready.then(applyScale);

    // Re-escalar cuando cambia el modo teclado (la altura objetivo
    // cambia de 1080 → 560). Observamos la clase con MutationObserver.
    const wrap = wrapRef.current;
    let mo: MutationObserver | null = null;
    if (wrap) {
      mo = new MutationObserver(applyScale);
      mo.observe(wrap, { attributes: true, attributeFilter: ['class'] });
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', applyScale);
        window.visualViewport.removeEventListener('scroll', applyScale);
      }
      window.removeEventListener('resize', applyScale);
      mo?.disconnect();
    };
  }, []);

  function press(id: string, handler: (() => void) | undefined) {
    return (e: React.PointerEvent) => {
      e.preventDefault();
      const el = e.currentTarget as HTMLElement;
      el.classList.add('btnPressed');
      // Tactile feedback: short vibration on supported devices.
      // Vibration API is a no-op on devices without it (desktop browsers).
      try {
        if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
          navigator.vibrate(10);
        }
      } catch { /* */ }
      handler?.();
      setTimeout(() => el.classList.remove('btnPressed'), 110);
    };
  }

  return (
    <div className="gameboy-wrap" ref={wrapRef}>
      <div className="gameboy power-on" id="gameboy" ref={gameboyRef}>
        <div className="front-plate">
          <div className="front-plate-head">
            <div className="vertical-stripe"></div>
            <div className="vertical-stripe"></div>
            <div className="vertical-stripe"></div>

            <div className="vertical-gouge vertical-gouge-1"></div>
            <div className="vertical-gouge vertical-gouge-2"></div>

            <div className="on-off">
              <div className="spike spike-left"><div></div></div>
              <div className="spike spike-right"><div></div></div>
              <span>OFF<i></i>ON</span>
            </div>
          </div>

          <div className="screen-container">
            <div className="screen-headline">
              <span>VICIN · COMMUNITY EDITION</span>
            </div>

            <div className="battery-light">
              <span>BATTERY</span>
            </div>

            <div className="screen">
              {/* React screens render here, styled as a GameBoy LCD.
                  Wrapper fills the entire .screen; inner stays at native
                  160x144 and is scaled up by --lcd-scale. */}
              <div className="gb-lcd-fill">
                <div className="gb-lcd-content">{children}</div>
              </div>
            </div>
          </div>

          <div id="controller">
            <div className="buttons-a-b">
              <div
                className="button-b button-key-j"
                id="controller_b"
                onPointerDown={press('b', onB)}
                role="button" aria-label="Botón B"
              ></div>
              <div
                className="button-a button-key-k"
                id="controller_a"
                onPointerDown={press('a', onA)}
                role="button" aria-label="Botón A"
              ></div>
            </div>

            <div
              className="start button-key-m"
              id="controller_start"
              onPointerDown={press('start', onStart)}
              role="button" aria-label="Start"
            ><div></div></div>

            <div
              className="select button-key-n"
              id="controller_select"
              onPointerDown={press('select', onSelect)}
              role="button" aria-label="Select"
            ><div></div></div>

            <div className="cross-container">
              <div className="spike"><div></div></div>
              <div className="spike"><div></div></div>
              <div className="spike"><div></div></div>
              <div className="spike"><div></div></div>

              <div className="cross" id="controller_dpad">
                <div className="top-down">
                  <div
                    className="button-top button-key-w"
                    id="controller_up"
                    onPointerDown={press('up', onUp)}
                    role="button" aria-label="Arriba"
                  >
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                  </div>
                  <div
                    className="button-bottom button-key-s"
                    id="controller_down"
                    onPointerDown={press('down', onDown)}
                    role="button" aria-label="Abajo"
                  >
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                  </div>
                </div>

                <div className="left-right">
                  <div
                    className="button-left button-key-a"
                    id="controller_left"
                    onPointerDown={press('left', onLeft)}
                    role="button" aria-label="Izquierda"
                  >
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                  </div>
                  <div
                    className="button-right button-key-d"
                    id="controller_right"
                    onPointerDown={press('right', onRight)}
                    role="button" aria-label="Derecha"
                  >
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                    <div className="button-stripe"></div>
                  </div>
                </div>
                <div className="cross-middle-bumb"></div>
              </div>
            </div>
          </div>

          <div className="speaker">
            <div><div className="speaker-inner-shadow"></div></div>
            <div><div className="speaker-inner-shadow"></div></div>
            <div><div className="speaker-inner-shadow"></div></div>
            <div><div className="speaker-inner-shadow"></div></div>
            <div><div className="speaker-inner-shadow"></div></div>
            <div><div className="speaker-inner-shadow"></div></div>
          </div>

          <div className="phones" id="volume-switch">
            <div className="vertical-stripe"></div>
            <div className="vertical-stripe"></div>
            <div className="vertical-stripe"></div>
            <i></i>
            <span>PHONES</span>
          </div>
        </div>

        <div className="power-button"><div></div></div>
      </div>
    </div>
  );
}

// Re-export the screen canvas font constants in case screens want to draw
// on a real <canvas> (future migration path).
export const GAMEBOY_FONTS = { FONT_HEAD, FONT_BODY } as const;
export const GAMEBOY_PALETTE = {
  lightest: '#9bbc0f',
  light:    '#8bac0f',
  dark:     '#306230',
  darkest:  '#0f380f',
} as const;